//back to
import axios from "axios";
import { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactFlagsSelect from "react-flags-select";
import { useLocation, useNavigate } from "react-router-dom";
import { useHotelInfoContext } from "../../../components/HotelInfoContext";
import Swal from "sweetalert2";
import { CurrencyContext } from "../../../components/CurrencyContext.js";
import "../../../styles/Room2.css";
import { isAllEmpty } from "../../../utils/isAllEmpty";
import { calculateTotalAmount } from "./modules/calculateTotalAmount";
import { formatCurrency } from "./modules/formatCurrency";
import { handleOnePayPayment } from "./modules/handleOnePayPayment";
import { handleOnePayReturn } from "./modules/handleOnePayReturn";
import { handleVNPayPayment } from "./modules/handleVNPayPayment";
import { handleVNPayReturn } from "./modules/handleVNPayReturn";
import { useBooking } from "./modules/useBooking";
import { useBraintree } from "./modules/useBraintree";
import { useBranding } from "./modules/useBranding";
import { useGuestInformation } from "./modules/useGuestInformation";
import { usePaypal } from "./modules/usePaypal";
import { AddOnServices } from "./molecules/addOnServices";
import { CreditCardNumber } from "./molecules/creditCardNumber";
import { RoomDetails } from "./molecules/roomDetails";
import { SecurityVerification } from "./molecules/securityVerification";
import { TermsAndConditionsCheckbox } from "./molecules/termsAndConditionsCheckbox";
import { TermsAndConditionsModal } from "./molecules/termsAndConditionsModal";
import { calculateTotalDays } from "../../../utils/calculateTotalDays";
import getCurrentUser from "../../../utils/getCurrentUser";

function BookingTemplate() {
  const { t } = useTranslation();
  const location = useLocation();
  const { bookingSidebar, childrenAgeRule } = location.state || {};
  const [cardInfo, setCardInfo] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCVC: "",
  });
  const { currency, convertAmount } = useContext(CurrencyContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);

  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const { fetchData, extraBedPrice, paymentMethod } = useBooking();
  const { initializeBraintree, handleBraintreePayment, braintreeRef } =
    useBraintree();

  const totaldays = useMemo(
    () =>
      bookingSidebar &&
      calculateTotalDays(bookingSidebar[0].fromdate, bookingSidebar[0].todate),
    [bookingSidebar]
  );
  const totalamount = useMemo(
    () =>
      calculateTotalAmount({
        bookingSidebar,
        extraBedPrice,
        totaldays,
      }),
    [bookingSidebar, extraBedPrice, totaldays]
  );
  const {
    handlePayPalOrder,
    guestNameRef,
    guestEmailRef,
    guestPhoneNumberRef,
    guestNationalityRef,
    specialRequestRef,
    loading,
    setLoading,
  } = usePaypal({
    bookingSidebar,
    navigate,
    currentUser,
    totalamount,
    totaldays,
    childrenAgeRule,
  });
  const { hotelInfo: ctxHotelInfo } = useHotelInfoContext() || {
    hotelInfo: null,
  };
  const {
    isValidEmail,
    validateEmail,
    validatePhoneNumber,
    errors,
    guestInfo,
    setGuestInfo,
    handleNationalityChange,
  } = useGuestInformation(guestNationalityRef);
  const { fetchBranding, branding } = useBranding();

  const getGuestDetails = () => ({
    guestName: guestNameRef.current.trim(),
    guestEmail: guestEmailRef.current.trim(),
    guestPhoneNumber: guestPhoneNumberRef.current.trim(),
    guestNationality: guestNationalityRef.current.trim(),
    specialRequest: specialRequestRef.current.trim(),
  });
  const displayExtraBedPrice = `${formatCurrency(
    extraBedPrice * totaldays,
    currency,
    convertAmount
  )} ${currency} / ${totaldays} nights`;
  const customParagraphStyle = {
    color: branding.paragraphColor,
    fontFamily: branding.paragraphFont,
  };

  useEffect(() => {
    // Fetch extra bed price and selected payment method from the backend
    fetchData();

    // Initialize Braintree Drop-in UI

    initializeBraintree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleVNPayReturn(setLoading, navigate, childrenAgeRule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    handleOnePayReturn(setLoading, navigate, childrenAgeRule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Warn user when they hit the browser back button from /booking to /home
  const _skipBackConfirm = useRef(false);
  useEffect(() => {
    // Push an extra history entry so the back button triggers popstate while
    // staying on this page first.
    window.history.pushState(null, "");

    const onPopState = (event) => {
      if (_skipBackConfirm.current) {
        // Allow navigation without prompting once (we already confirmed)
        _skipBackConfirm.current = false;
        return;
      }

      // Show confirmation modal
      event.preventDefault();
      Swal.fire({
        title: t("booking.confirm_to_leave"),
        text: t("booking.go_back_discard"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("booking.confirm_booking"),
        cancelButtonText: t("booking.cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          // Allow the navigation: set flag and go back again so browser proceeds
          _skipBackConfirm.current = true;
          navigate("/", {
            state: {
              isReset: true,
            },
          });
        } else {
          // User chose to stay: re-push state to remain on the same page
          window.history.pushState(null, "");
        }
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
    // empty deps so listener is active while this component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleBooking = async () => {
    const {
      guestName,
      guestEmail,
      guestPhoneNumber,
      guestNationality,
      specialRequest,
    } = guestInfo;
    console.log("Guest Name:", guestName);
    console.log("Guest Email:", guestEmail);
    console.log("Guest Phone Number:", guestPhoneNumber);
    console.log("Guest Nationality:", guestNationality);
    console.log("Guest Details:", getGuestDetails());
    const nationality = guestNationalityRef.current.trim();

    if (!guestName || !guestEmail || !guestPhoneNumber || !guestNationality) {
      Swal.fire("Error", t("guest_information.require_all_fields"), "error");
      return;
    }
    setLoading(true);
    try {
      let braintreeNonce = null;

      if (paymentMethod === "vnpay") {
        await handleVNPayPayment(
          bookingSidebar,
          totalamount,
          totaldays,
          getGuestDetails(),
          setLoading
        );
      }

      if (paymentMethod === "braintree") {
        braintreeNonce = await handleBraintreePayment(); // Braintree nonce
      }

      const bookingDetails = {
        bookingSidebar,
        userid: currentUser ? currentUser._id : "guest",
        totalamount,
        totaldays,
        guestName,
        guestEmail,
        guestPhoneNumber,
        guestNationality: nationality,
        specialRequest,
        paymentMethod,
        ...(paymentMethod === "creditCard" && {
          cardNumber: cardInfo.cardNumber,
          cardExpiry: cardInfo.cardExpiry,
          cardCVC: cardInfo.cardCVC,
        }),
        ...(paymentMethod === "braintree" && { braintreeNonce }),
      };

      console.log("Booking Details sent to backend:", bookingDetails); // Log booking details
      const response = await axios.post(
        "/api/bookings/bookroom",
        bookingDetails
      );
      const confirmationUrl = response.data.confirmationUrl;
      Swal.fire({
        title: t("booking.success"),
        text: t("booking.success_message"),
        icon: "success",
        confirmButtonText: t("booking.OK"),
      }).then(async () => {
        // Use provider hotel info instead of fetching
        const hotelInfo = ctxHotelInfo;

        // Navigate to ConfirmationScreen with state
        navigate("/confirmation", {
          state: { booking: response.data.booking, hotelInfo, childrenAgeRule },
        });

        // Redirect to confirmationUrl (fallback)
        if (confirmationUrl) {
          window.location.href = confirmationUrl;
        }
      });
    } catch (error) {
      console.error("Booking error:", error);
      Swal.fire("Oops, Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      guestName,
      guestEmail,
      confirmGuestEmail,
      guestPhoneNumber,
      confirmGuestPhoneNumber,
      guestNationality,
    } = guestInfo;
    // Validation for mismatched email and phone number
    if (guestEmail !== confirmGuestEmail) {
      Swal.fire(
        "Error",
        t("guest_information.email_confirmation_not_match"),
        "error"
      );
      return;
    }

    if (guestPhoneNumber !== confirmGuestPhoneNumber) {
      Swal.fire(
        "Error",
        t("guest_information.phone_confirmation_not_match"),
        "error"
      );
      return;
    }

    if (!guestName || !guestEmail || !guestPhoneNumber || !guestNationality) {
      Swal.fire("Error", t("guest_information.require_all_fields"), "error");
      return;
    }

    if (paymentMethod === "paypal") {
      handlePayPalOrder(); // Trigger PayPal order handling
    } else if (paymentMethod === "vnpay") {
      await handleVNPayPayment(
        bookingSidebar,
        totalamount,
        totaldays,
        getGuestDetails(),
        setLoading
      ); // Trigger VNPay payment handling
    } else if (paymentMethod === "onepay") {
      await handleOnePayPayment(
        bookingSidebar,
        totalamount,
        totaldays,
        getGuestDetails(),
        setLoading
      ); // Trigger OnePay payment handling
    } else {
      handleBooking(); // Trigger booking for other payment methods
    }
  };

  if (!bookingSidebar || bookingSidebar.length === 0) {
    return <div>{t("guest_information.no_booking_available")}</div>;
  }

  return (
    <div
      className="background"
      style={{
        backgroundImage: `url(${branding.pageBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="container">
        <div className="row mt-5 gap-3">
          <div
            className="glass-effect2 col-md-6"
            style={{
              background: branding.searchBarGradient,
            }}
          >
            <h1 style={{ color: "#FAF3FF" }}>
              {t("booking.your_booking_details")}
            </h1>
            {bookingSidebar.length > 0 && (
              <div className="text-white">
                <p>
                  <strong>{t("booking.check_in")}:</strong>{" "}
                  {bookingSidebar[0].fromdate}
                </p>
                <p>
                  <strong>{t("booking.check_out")}:</strong>{" "}
                  {bookingSidebar[0].todate}
                </p>
                <p>
                  <strong>{t("booking.total_nights")}:</strong> {totaldays}
                </p>
              </div>
            )}
            <ul className="list-group mb-4">
              {/* Display Rooms */}
              <RoomDetails
                bookingSidebar={bookingSidebar}
                branding={branding}
                displayExtraBedPrice={displayExtraBedPrice}
                currency={currency}
                convertAmount={convertAmount}
              />

              {/* Children Age Policy & Extra Bed Price */}
              <div
                className="mt-3 p-3"
                style={{ background: "#f8f9fa", borderRadius: "8px" }}
              >
                <h5 style={{ color: "#433d80" }}>
                  {t("booking.children_age_policy.title")}
                </h5>
                <p style={{ color: "#433d80", marginBottom: 0 }}>
                  {t("booking.children_age_policy.description")}
                </p>
                <p style={{ color: "#433d80", marginBottom: 0 }}>
                  <strong>
                    {t("booking.children_age_policy.extra_bed_price")}:
                  </strong>{" "}
                  {formatCurrency(
                    childrenAgeRule.extraPrice,
                    currency,
                    convertAmount
                  )}{" "}
                  {currency} / night
                </p>
              </div>

              {/* Display Add-On Services */}
              <h5 className="mt-3 text-white">{t("booking.addon_services")}</h5>
              <AddOnServices
                bookingSidebar={bookingSidebar}
                branding={branding}
                currency={currency}
                convertAmount={convertAmount}
              />
            </ul>
            <h3 className="bs">
              <strong>
                {t("booking.grand_total")}:{" "}
                <span style={customParagraphStyle}>
                  {formatCurrency(totalamount, currency, convertAmount)}
                </span>{" "}
                {currency}{" "}
              </strong>
            </h3>
            <h6 className="bs">
              <strong>
                {t("booking.total_pay")}:{" "}
                <span style={customParagraphStyle}>{totalamount}</span> VND{" "}
              </strong>
            </h6>
          </div>

          <div
            className="glass-effect2 col-md-5 text-white"
            style={{
              background: branding.searchBarGradient,
            }}
          >
            <h1>{t("guest_information.guest_information")}</h1>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="guestName" className="form-label">
                  <strong>
                    {" "}
                    <span class="text-danger">*</span>
                    {t("guest_information.guest_name")}
                  </strong>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="guestName"
                  placeholder={t("guest_information.enter_your_name")}
                  value={guestInfo.guestName}
                  ref={guestNameRef}
                  onChange={(e) => {
                    setGuestInfo((prev) => ({
                      ...prev,
                      guestName: e.target.value,
                    }));
                    guestNameRef.current = e.target.value;
                  }}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="guestEmail" className="form-label">
                  <strong>
                    {" "}
                    <span class="text-danger">*</span>
                    {t("guest_information.email_address")}
                  </strong>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="guestEmail"
                  placeholder={t("guest_information.enter_your_email")}
                  value={guestInfo.guestEmail}
                  onChange={(e) => {
                    setGuestInfo((prev) => ({
                      ...prev,
                      guestEmail: e.target.value,
                    }));
                    guestEmailRef.current = e.target.value;
                  }}
                  required
                />
                {!isValidEmail && (
                  <small className="text-danger">
                    {t("guest_information.enter_valid_email")}
                  </small>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="confirmGuestEmail" className="form-label">
                  <strong>
                    {" "}
                    <span class="text-danger">*</span>
                    {t("guest_information.confirm_email_address")}
                  </strong>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="confirmGuestEmail"
                  placeholder={t("guest_information.confirm_your_email")}
                  value={guestInfo.confirmGuestEmail}
                  onChange={(e) =>
                    setGuestInfo((prev) => ({
                      ...prev,
                      confirmGuestEmail: e.target.value,
                    }))
                  }
                  onBlur={validateEmail}
                  required
                />
                {errors.email && (
                  <small className="text-danger">{errors.email}</small>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="guestPhoneNumber" className="form-label">
                  <strong>
                    {" "}
                    <span class="text-danger">*</span>
                    {t("guest_information.phone_number")}
                  </strong>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="guestPhoneNumber"
                  placeholder={t("guest_information.enter_your_phone")}
                  value={guestInfo.guestPhoneNumber}
                  onChange={(e) => {
                    setGuestInfo((prev) => ({
                      ...prev,
                      guestPhoneNumber: e.target.value,
                    }));
                    guestPhoneNumberRef.current = e.target.value;
                  }}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="confirmGuestPhoneNumber" className="form-label">
                  <strong>
                    {" "}
                    <span class="text-danger">*</span>
                    {t("guest_information.confirm_phone_number")}
                  </strong>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="confirmGuestPhoneNumber"
                  placeholder={t("guest_information.confirm_your_phone")}
                  value={guestInfo.confirmGuestPhoneNumber}
                  onChange={(e) =>
                    setGuestInfo((prev) => ({
                      ...prev,
                      confirmGuestPhoneNumber: e.target.value,
                    }))
                  }
                  onBlur={validatePhoneNumber}
                  required
                />
                {errors.phone && (
                  <small className="text-danger">{errors.phone}</small>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="guestNationality" className="form-label">
                  <strong>
                    <span class="text-danger">*</span>
                    {t("guest_information.nationality")}
                  </strong>
                </label>
                <ReactFlagsSelect
                  selected={guestInfo.guestNationality} // Use state value for UI
                  onSelect={handleNationalityChange} // Use the handler function
                  searchable
                  showSelectedLabel={true}
                  placeholder={t("guest_information.select_nationality")}
                  className="form-control nationality-dropdown"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="specialRequest" className="form-label">
                  <strong>
                    <span class="text-danger">*</span>
                    {t("guest_information.special_requests")}
                  </strong>
                </label>
                <textarea
                  className="form-control"
                  id="specialRequest"
                  rows="3"
                  placeholder={t("guest_information.any_special_requests")}
                  value={guestInfo.specialRequest}
                  onChange={(e) => {
                    setGuestInfo((prev) => ({
                      ...prev,
                      specialRequest: e.target.value,
                    }));
                    specialRequestRef.current = e.target.value;
                  }}
                />
              </div>

              {/* Payment Method Section */}
              <h1 className="mt-3">{t("booking.check_out")}</h1>
              {paymentMethod === "creditCard" && (
                <CreditCardNumber
                  cardInfo={cardInfo}
                  setCardInfo={setCardInfo}
                />
              )}

              {paymentMethod === "Pay At Hotel" && (
                <p>{t("booking.pay_at_hotel")}</p>
              )}
              {paymentMethod === "braintree" && (
                <div className="mb-3">
                  <div ref={braintreeRef} />
                </div>
              )}

              {/* Terms and Conditions Checkbox */}
              {paymentMethod !== "paypal" && (
                <TermsAndConditionsCheckbox
                  isTermsChecked={isTermsChecked}
                  setIsTermsChecked={setIsTermsChecked}
                  setIsModalVisible={setIsModalVisible}
                />
              )}

              {/* Modal for Terms and Conditions */}
              <TermsAndConditionsModal
                isModalVisible={isModalVisible}
                setIsModalVisible={setIsModalVisible}
              />

              {/* Show PayPal button for PayPal payment */}
              {paymentMethod === "paypal" ? (
                <div
                  id="paypal-button-container"
                  style={{ marginBottom: "20px" }}
                ></div>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary w-100 mb-5 bs"
                  disabled={
                    !isTermsChecked ||
                    loading ||
                    !isAllEmpty(errors) ||
                    !isValidEmail ||
                    !guestInfo.guestNationality ||
                    !guestInfo.specialRequest
                  }
                  style={{
                    marginBottom: "15px",
                    marginTop: "1rem",
                    color: "#4d4692",
                    fontWeight: "800",
                  }}
                >
                  {loading ? "Processing..." : "BOOK NOW"}
                </button>
              )}

              {/* Security Verification Section */}
              <SecurityVerification />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingTemplate;
